import ApiService from "src/util/api.service";
import { getLanguageHeaders } from "./commonLanguageHeader";
import { ServicesUrlEndpoints } from "./servicesEndpointConstant";

export interface UserNamedReference {
  id?: string | number;
  name?: string;
  companyName?: string;
}

export interface User {
  id: string | number;
  userId?: string | number;
  username?: string;
  name?: string;
  surname?: string;
  email?: string;
  phoneNumber?: string;
  userRole?: UserNamedReference | null;
  client?: UserNamedReference | null;
  distributor?: UserNamedReference | null;
  language?: UserNamedReference | null;
  active?: boolean;
  state?: string;
  createDate?: string;
  [key: string]: unknown;
}

export interface UserFormPayload {
  id?: string | number;
  name: string;
  surname: string;
  username: string;
  email: string;
  password?: string;
  clientId?: string | number;
  userRoleId?: string | number;
  languageId?: string | number;
  distributorId?: string | number;
}

export interface UserRole {
  id: string | number;
  name?: string;
  code?: string;
}

export interface UserSystem {
  id: string | number;
  name: string;
  code?: string;
  isChecked?: boolean;
}

export interface UserActivityPermission {
  activityId: string | number;
  code?: string;
  activityName?: string;
  activityCode?: string;
  enabled?: boolean;
  canView?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
  upselling?: boolean;
  [key: string]: unknown;
}

export interface UserActivityPermissionPayload {
  userId: string | number;
  activityId: string | number;
  enabled: boolean;
  canView: boolean;
  canEdit: boolean;
  canDelete: boolean;
}

export interface UserDetailResponse {
  user: User;
  activityActionRights?: UserActivityPermission[];
  activityBlocks?: unknown[];
  visibleClients?: UserNamedReference[];
  allowedAllClients?: boolean;
  [key: string]: unknown;
}

export interface UserDevice {
  id: string | number;
  licensePlate?: string;
  name?: string;
  imei?: string;
  isChecked?: boolean;
}

export interface UserTimedDataPermission {
  settingId: string | number;
  code?: string;
  name?: string;
  isSelected?: boolean;
  monthAgo?: number;
}

export interface UserTimedDataPayload {
  userId: string | number;
  enabled: boolean;
  timedDataPermissionId?: number;
  monthAgo?: number;
}

export interface UserListParams {
  page?: number;
  size?: number;
  sortBy?: string;
  sort?: "ASC" | "DESC";
  status?: string;
  search?: string;
  [key: string]: unknown;
}

export interface ResendAccessEmailPayload {
  username: string;
  platform: "FDM";
}

export interface UserStatusParams {
  state: "ACTIVE" | "BLOCKED";
  customerId?: string | number;
}

export interface UserActiveStatusPayload {
  id: string | number;
  active: boolean;
}

export interface UserListResponse {
  users?: User[];
  totalItems?: number;
  totalPages?: number;
  currentPage?: number;
}

export const getUsersList = (params: UserListParams = {}, language?: string) =>
  ApiService.get<{ response: UserListResponse }>(ServicesUrlEndpoints.USER.LISTING, {
    params,
    ...getLanguageHeaders(language),
  });

export const updateUserStatus = (
  id: string | number,
  params: UserStatusParams,
  language?: string
) =>
  ApiService.patch(ServicesUrlEndpoints.USER.UPDATE_STATUS.replace("{userId}", String(id)), null, {
    params,
    ...getLanguageHeaders(language),
  });

export const updateUserActiveStatus = (
  id: string | number,
  payload: UserActiveStatusPayload,
  language?: string
) =>
  ApiService.patch(
    ServicesUrlEndpoints.USER.UPDATE.replace("{userId}", String(id)),
    payload,
    {
      ...getLanguageHeaders(language),
    }
  );

export const deleteUser = (id: string | number, language?: string) =>
  ApiService.delete(ServicesUrlEndpoints.USER.DELETE.replace("{userId}", String(id)), {
    ...getLanguageHeaders(language),
  });

export const resendAccessEmail = (payload: ResendAccessEmailPayload, language?: string) =>
  ApiService.post(
    ServicesUrlEndpoints.USER.RESEND_EMAIL,
    {},
    {
      params: payload,
      ...getLanguageHeaders(language),
    }
  );

export const getUserById = (id: string | number, language?: string) =>
  ApiService.get<{ response: UserDetailResponse }>(
    ServicesUrlEndpoints.USER.GET.replace("{userId}", String(id)),
    {
      ...getLanguageHeaders(language),
    }
  );

export const createUser = (payload: UserFormPayload, language?: string) =>
  ApiService.post<{ response: User }>(ServicesUrlEndpoints.USER.CREATE, payload, {
    ...getLanguageHeaders(language),
  });

export const updateUser = (id: string | number, payload: UserFormPayload, language?: string) =>
  ApiService.patch<{ response: User }>(
    ServicesUrlEndpoints.USER.UPDATE.replace("{userId}", String(id)),
    { ...payload, id },
    {
      ...getLanguageHeaders(language),
    }
  );

export const getUserRoleLookup = (language?: string) =>
  ApiService.get<{ response: UserRole[] }>(ServicesUrlEndpoints.USER.ROLE_LOOKUP, {
    ...getLanguageHeaders(language),
  });

export const getDistributorList = (language?: string) =>
  ApiService.get<{ response: UserNamedReference[] }>(
    ServicesUrlEndpoints.USER_LEVEL_ACCESS.LOOKUP.DISTRIBUTOR_LIST,
    {
      ...getLanguageHeaders(language),
    }
  );

export const getClientLanguage = (clientId: string | number, language?: string) =>
  ApiService.get<{ response: UserNamedReference }>(
    ServicesUrlEndpoints.USER.CLIENT_LANGUAGE.replace("{clientId}", String(clientId)),
    {
      ...getLanguageHeaders(language),
    }
  );

export const getUserSystems = (userId: string | number, language?: string) =>
  ApiService.get<{ response: UserSystem[] }>(ServicesUrlEndpoints.USER.SYSTEMS, {
    params: { userId },
    ...getLanguageHeaders(language),
  });

export const getUserActivities = (
  userId: string | number,
  systemId: string | number,
  language?: string
) =>
  ApiService.get<{ response: UserActivityPermission[] }>(
    ServicesUrlEndpoints.USER.ACTIVITIES.replace("{userId}", String(userId)).replace(
      "{systemId}",
      String(systemId)
    ),
    {
      ...getLanguageHeaders(language),
    }
  );

export const saveUserActivities = (payload: UserActivityPermissionPayload[], language?: string) =>
  ApiService.post(ServicesUrlEndpoints.USER.SAVE_ACTIVITIES, payload, {
    ...getLanguageHeaders(language),
  });

export const saveAllowedSystem = (
  userId: string | number,
  systemTypeId: string | number,
  enable: boolean,
  language?: string
) =>
  ApiService.post(
    ServicesUrlEndpoints.USER.SAVE_ALLOWED_SYSTEM.replace("{userId}", String(userId)),
    undefined,
    {
      params: { systemTypeId, enable },
      ...getLanguageHeaders(language),
    }
  );

export const getUserDevices = (userId: string | number, language?: string) =>
  ApiService.get<{ response: UserDevice[] }>(ServicesUrlEndpoints.USER.DEVICES, {
    params: { userId },
    ...getLanguageHeaders(language),
  });

export const saveUserVehicles = (
  userId: string | number,
  vehicleIds: Array<string | number>,
  language?: string
) =>
  ApiService.post(
    ServicesUrlEndpoints.USER.SAVE_VEHICLES.replace("{userId}", String(userId)),
    { vehicles: vehicleIds },
    {
      ...getLanguageHeaders(language),
    }
  );

export const getUserTimedData = (userId: string | number, language?: string) =>
  ApiService.get<{ response: UserTimedDataPermission[] }>(
    ServicesUrlEndpoints.USER.TIMED_DATA_PERMISSION_BY_USER.replace("{userId}", String(userId)),
    {
      ...getLanguageHeaders(language),
    }
  );

export const saveUserTimedData = (payload: UserTimedDataPayload, language?: string) =>
  ApiService.post(ServicesUrlEndpoints.USER.TIMED_DATA_PERMISSION, payload, {
    ...getLanguageHeaders(language),
  });
